import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import type { OrderFiltering } from "~/components/order-table/use-filters";
import { useFilters } from "~/components/order-table/use-filters";
import { useSorting } from "~/components/order-table/use-sorting";
import { OrderShopifyErrorList } from "~/components/order/order-shopify-error-list";
import { Button } from "~/components/ui/button";
import { ExternalLinkSimpleIcon } from "~/components/ui/icons/external-link-simple";
import { RefreshIcon } from "~/components/ui/icons/refresh";
import { ShopifyErrorIcon } from "~/components/ui/icons/shopify-error";
import {
  OrderTagBrand,
  OrderTagFulfillment,
  OrderTagShopify,
  OrdersSortFieldInput,
  OrdersSortOrderInput,
  useOrdersUnfulfilledDesyncShopifyQuery,
} from "~/graphql/graphql-new.gen";
import { activeSaleIdState } from "~/stores/sales";
import { getProcShopifyUrl, getShopifyErrors } from "~/utils/shopify";

import { usePagination } from "./use-pagination";
import { useResyncShopifyOrders } from "./use-resync-shopify-orders";
import { useSearch } from "./use-search";
import { TabEntry, useTabs } from "./use-tabs";

export const OrdersShopifyDesyncAlert: React.FC = () => {
  const { i18n, t } = useTranslation();
  const { mutateAsync: onResyncShopifyOrders, isPending } =
    useResyncShopifyOrders();
  const { resetPagination } = usePagination();
  const { setFilters, trackFilters } = useFilters();

  const { resetSearch } = useSearch();
  const [_, setSearchParams] = useSearchParams();
  const { resetSorting } = useSorting();
  const { setSelectedTab } = useTabs();

  const activeSaleId = useAtomValue(activeSaleIdState);

  const { data } = useOrdersUnfulfilledDesyncShopifyQuery({
    saleId: activeSaleId ?? "",
    input: {
      filter: {
        tagging: {
          brand: [OrderTagBrand.Available],
          fulfillment: [OrderTagFulfillment.Unfulfilled],
          shopify: [OrderTagShopify.Unsynchronized],
        },
      },
      paging: {
        pageSize: 100,
      },
      sort: {
        field: OrdersSortFieldInput.TagShopify,
        order: OrdersSortOrderInput.Ascending,
      },
    },
  });

  if (!data || data.orders.totalCount === 0) return null;

  const shopifyDesyncErrors = getShopifyErrors(
    data.orders.nodes.map((order) => order.integration.errorMessage ?? ""),
  );

  const filterOrders = () => {
    const unsynchronizedFilters: OrderFiltering = {
      claim: [],
      fulfillment: [],
      claimReason: [],
      refund: [],
      reship: [],
      return: [],
      shopify: [OrderTagShopify.Unsynchronized],
      shipment: [],
    };
    resetPagination();
    resetSearch();
    resetSorting(TabEntry.Filters);
    setFilters(unsynchronizedFilters);
    setSearchParams((prev) => {
      prev.delete("after");
      prev.delete("before");
      prev.delete("q");
      prev.delete("sort");
      prev.set("view", TabEntry.Filters.toString());
      prev.set("filters", JSON.stringify(unsynchronizedFilters));
      return prev;
    });
    trackFilters(unsynchronizedFilters);
    setSelectedTab(TabEntry.Filters);
  };

  return (
    <div className="flex flex-col justify-between gap-x-8 gap-y-6 rounded border border-red-350 bg-[#F9E7E7] p-6 lg:flex-row lg:items-center">
      <div className="flex flex-row space-x-4">
        <ShopifyErrorIcon className="size-6 shrink-0 text-red-600" />
        <div className="space-y-2.5">
          <div className="space-y-1">
            <div className="text-sm font-bold text-gray-700">
              {t("orders.shopify_desync.x_desynced_orders", {
                count: data?.orders.totalCount,
              })}
            </div>
            <OrderShopifyErrorList shopifyDesyncErrors={shopifyDesyncErrors} />
          </div>
          <a
            href={getProcShopifyUrl(i18n.language)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center space-x-0.5 text-xs font-semibold text-green-900"
          >
            <span>{t("orders.shopify_desync.view_procedure")}</span>
            <ExternalLinkSimpleIcon className="size-4" />
          </a>
        </div>
      </div>
      <div className="flex shrink-0 flex-col gap-4 lg:flex-row">
        <Button
          variant="outline"
          type="button"
          onClick={filterOrders}
          className="flex items-center justify-center space-x-2 rounded-lg border-2 border-[#E79E9E] bg-white px-4 py-2 text-sm font-bold text-gray-900"
        >
          <span>{t("orders.shopify_desync.show_orders")}</span>
        </Button>
        <Button
          type="button"
          className="justify-center space-x-2"
          onClick={onResyncShopifyOrders}
          disabled={isPending}
        >
          <RefreshIcon className="size-5 shrink-0" />
          <span>{t("orders.shopify_desync.sync_orders")}</span>
        </Button>
      </div>
    </div>
  );
};
